import { Grid, MenuItem, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import { ShareDialogSelect } from "./ShareDialogSelect";
import { useCurrentDashboard } from "../../../useCurrentDashboard";
import { AuthStore } from "stores/AuthStore";
import { IDashboardTenantVisibility, IDashboardUserPermission } from "components/Signals/SignalsModels";

// Component for managing and displaying tenant visibility settings
export const TenantVisibilitySection = ({
    isCurrentUserOwner,
    userPermissions,
}: {
    isCurrentUserOwner: boolean;
    userPermissions: IDashboardUserPermission[];
}) => {
    const currentDashboard = useCurrentDashboard();
    const authStore = useStore(AuthStore);
    const theme = useTheme();
    const [tenantVisibility, setTenantVisibility] = useState(
        currentDashboard?.tenantVisibility,
    );

    const tenantVisibilityOptions = [
        {
            id: -1,
            value: -1,
            name: "Restricted",
            description: "Only you can access this dashboard.",
        },
        {
            id: IDashboardTenantVisibility.Restricted,
            value: IDashboardTenantVisibility.Restricted,
            name: "Restricted",
            description: "Only those invited can access this dashboard.",
        },
        {
            id: IDashboardTenantVisibility.PubliclyViewable,
            value: IDashboardTenantVisibility.PubliclyViewable,
            name: "Can View",
            description: `Anyone at ${authStore.orgStore.selectedOrganization?.name} can view this dashboard.`,
        },
        {
            id: IDashboardTenantVisibility.PubliclyEditable,
            value: IDashboardTenantVisibility.PubliclyEditable,
            name: "Can Edit",
            description: `Anyone at ${authStore.orgStore.selectedOrganization?.name} can view and edit this dashboard.`,
        },
    ];

    const onTenantVisibilityChange = async (
        value: IDashboardTenantVisibility | undefined,
    ) => {
        if (!currentDashboard || value === undefined) return;
        const currentValue = currentDashboard.tenantVisibility;
        try {
            await currentDashboard.updateDashboardVisibility(value);
            setTenantVisibility(value);
        } catch (e) {
            setTenantVisibility(currentValue);
        }
    };
    return (
        <Grid sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography
                    sx={{
                        color: theme.palette.gray[700],
                        fontSize: "14px",
                        fontWeight: theme.typography.fontWeightSemibold,
                        lineHeight: "20px",
                    }}
                >
                    Org-Wide Access
                </Typography>
                {isCurrentUserOwner ? (
                    <ShareDialogSelect<IDashboardTenantVisibility | undefined>
                        id="tenant-visibility-select"
                        value={tenantVisibility}
                        onChange={async (value) => {
                            await onTenantVisibilityChange(value);
                        }}
                    >
                        {tenantVisibilityOptions.slice(1).map((option) => (
                            <MenuItem
                                id={`tenant-visibility-option-${option.value}`}
                                key={option.name}
                                value={option.value}
                                sx={{
                                    fontWeight:
                                        theme.typography.fontWeightSemibold,
                                    color:
                                        tenantVisibility === option.value
                                            ? theme.palette.primary.main
                                            : theme.palette.gray[600],
                                    fontSize: "14px",
                                    "&:hover": {
                                        backgroundColor:
                                            theme.palette.primary.light,
                                        color: theme.palette.primary.main,
                                    },
                                }}
                            >
                                {option.name}
                            </MenuItem>
                        ))}
                    </ShareDialogSelect>
                ) : (
                    <Typography
                        sx={{
                            color: theme.palette.gray[700],
                            fontWeight: theme.typography.fontWeightSemibold,
                        }}
                    >
                        {
                            tenantVisibilityOptions.find(
                                (option) => option.value === tenantVisibility,
                            )?.name
                        }
                    </Typography>
                )}
            </Grid>
            <Typography>
                {userPermissions && userPermissions.length === 1
                    ? tenantVisibility === // If their are no user permissions and the tenant visibility is set to Restricted, default to the No Access option
                      IDashboardTenantVisibility.Restricted
                        ? tenantVisibilityOptions[0].description
                        : tenantVisibilityOptions.find(
                              (option) => option.value === tenantVisibility,
                          )?.description
                    : tenantVisibilityOptions.find(
                          (option) => option.value === tenantVisibility,
                      )?.description}
            </Typography>
        </Grid>
    );
};
